import React, {useState} from 'react';
import {ERROR} from '@di-shared/const';
import DailyLineGraph from '@di-shared/features/common/DailyLineGrpah';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {MAX_GRAPH_LENGTH} from '@di-shared/features/safe-driving-vDSP/SafeDrivingContents/.kr';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {addDate, subDate} from '@shared/dateUtils';

export interface SafeDrivingDailyGraphKrProps {
  data: Array<{
    score: number;
    scoreDate: Date | null;
  }>;
}

export default function ({data}: SafeDrivingDailyGraphKrProps) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [selectedDataIndex, setSelectedDataIndex] = useState<number>(data.length - 1);

  function getTooltipText(selectedDataIndex: number) {
    const selectedScoreDate = data[selectedDataIndex].scoreDate;
    if (!selectedScoreDate) {
      throw Error('Invalid Date');
    }
    return `${CldrDatetimeFormatter.format(selectedScoreDate, 'MMMd')}┃${data[selectedDataIndex].score}${t('T10056')}`;
  }

  function getSafeDrivingDiffMessage() {
    let message = '';
    function getSelectedDataFormat(date: Date) {
      return CldrDatetimeFormatter.format(date, 'MMMd');
    }

    const todayScore =
      data[
        data.findIndex((dateItem) => {
          if (!dateItem.scoreDate) {
            throw Error('Invalid Date');
          }
          today.setHours(0, 0, 0, 0);
          dateItem.scoreDate.setHours(0, 0, 0, 0);
          return today.getTime() === dateItem.scoreDate.getTime();
        })
      ]?.score || -1;

    const {score: selectedScore, scoreDate: selectedScoreDate} = data[selectedDataIndex];
    if (selectedScoreDate === null || selectedScore === null) {
      throw Error('Selected Point is Invalid Point');
    }

    if (getSelectedDataFormat(selectedScoreDate) === getSelectedDataFormat(new Date())) {
      message = t('T20184_$score', {score: selectedScore || ERROR.DASH});
    } else if (todayScore === -1) {
      message = t('T20187');
    } else if (selectedScore === todayScore) {
      message = t('T20185_$date', {
        date: getSelectedDataFormat(selectedScoreDate),
      });
    } else if (selectedScore > todayScore) {
      message = t('T20186_$date_$score', {
        date: getSelectedDataFormat(selectedScoreDate),
        score: selectedScore - todayScore,
      });
    } else if (selectedScore < todayScore) {
      message = t('T20012_$date_$score', {
        date: getSelectedDataFormat(selectedScoreDate),
        score: todayScore - selectedScore,
      });
    } else {
      message = t('T20187');
    }
    return message;
  }

  return (
    <>
      <p className="section_container_desc">{getSafeDrivingDiffMessage()}</p>

      <DailyLineGraph
        data={data}
        labels={Array.from({length: MAX_GRAPH_LENGTH}).map((_, i) => addDate(subDate(today, {days: MAX_GRAPH_LENGTH - 1}), {days: i}))}
        options={{
          max: 100,
          tooltip: {
            isShow: true,
            tooltipText: getTooltipText(selectedDataIndex),
          },
        }}
        interactions={{
          selectedDataIndex,
          onUpdateSelectedDataIndex: setSelectedDataIndex,
        }}
      />
    </>
  );
}
